import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type TrialBalanceTotalsProps = {
    /** Pre-formatted `₾` strings — no arithmetic in the render layer. */
    totalDebit: string;
    totalCredit: string;
    /** The **server's** verdict, carried through the hook untouched. */
    balanced: boolean;
    /** By how much the two totals disagree. Only read when `balanced` is false. */
    differenceText: string;
};

const Total: React.FC<{ label: string; value: string; slot: string }> = ({
    label,
    value,
    slot,
}) => (
    <div className="flex flex-col">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        <span
            className="text-base font-semibold tabular-nums text-foreground"
            {...getNodeTestIdProp(`trial-balance-total-${slot}`)}
        >
            {value}
        </span>
    </div>
);

/**
 * Total debits, total credits and the balanced verdict — the point of the
 * screen.
 *
 * Deliberately the **same visual language** as the journal entry form's
 * `TotalsBar` and the balance sheet's `BalanceCheckBar`: two figures on the left,
 * a calm success state or the explicit gap on the right, state carried by text
 * and icon rather than by colour alone. It means the same thing to the same
 * reader in all three places, and consistency beats novelty here.
 *
 * It is a fourth copy of that markup rather than a shared component because each
 * of the three hardcodes its own literal testid prefix and owns its own copy —
 * the entry form has a third state ("enter amounts") that a posted ledger cannot
 * be in, and the balance sheet compares two *statement* totals rather than two
 * columns. Collapsing them is a refactor of two shipped screens; it is noted in
 * `pages/accounting/README.md` rather than smuggled into this task.
 *
 * The verdict itself is never computed here or in the hook. Every entry is
 * validated as balanced before it can post, so this row is a diagnostic of the
 * posting engine — and a client-side recomputation could only disagree with the
 * ledger about the ledger.
 */
export const TrialBalanceTotals: React.FC<TrialBalanceTotalsProps> = ({
    totalDebit,
    totalCredit,
    balanced,
    differenceText,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-surface-secondary px-4 py-3">
            <div className="flex items-center gap-8">
                <Total
                    label={t('trialBalance.totals.debit')}
                    slot="debit"
                    value={totalDebit}
                />
                <Total
                    label={t('trialBalance.totals.credit')}
                    slot="credit"
                    value={totalCredit}
                />
            </div>

            <div
                className={cn(
                    'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold',
                    balanced
                        ? 'bg-success-soft text-success-soft-foreground'
                        : 'bg-danger-soft text-danger-soft-foreground',
                )}
                {...getNodeTestIdProp('trial-balance-balance-indicator')}
            >
                {balanced ? (
                    <>
                        <Icon aria-hidden icon="gravity-ui:circle-check" />
                        {t('trialBalance.totals.balanced')}
                    </>
                ) : (
                    <>
                        <Icon aria-hidden icon="gravity-ui:scales" />
                        {t('trialBalance.totals.offBy', {
                            amount: differenceText,
                        })}
                    </>
                )}
            </div>
        </div>
    );
};
